import {
  HouseholdRespondToChatMessageInput,
  MessageResponseAction,
} from '@/types/schema';

/** Which multiple-choice question shape the shared renderer is showing. */
export type MultipleChoiceVariant = 'singleSelect' | 'multiSelect';

/**
 * The user's in-progress answer to a multiple-choice question. The "Other"
 * free-form answer is modeled as a flag beside the declared option values —
 * not as a sentinel value inside `selectedValues` — so it can never collide
 * with a value the backend declared.
 *
 * Invariant: `isOtherSelected` and a non-empty `selectedValues` are mutually
 * exclusive, because the respond mutation rejects `freeFormText` combined
 * with `responseOptionValues`. The toggle helpers below maintain this.
 */
export interface MultipleChoiceSelection {
  selectedValues: string[];
  isOtherSelected: boolean;
}

export const EMPTY_MULTIPLE_CHOICE_SELECTION: MultipleChoiceSelection = {
  selectedValues: [],
  isOtherSelected: false,
};

/**
 * Applies a click on a declared option. Single-select replaces the selection
 * (radio semantics: re-clicking the selected option keeps it selected);
 * multi-select toggles the option. Either way "Other" is deselected — a
 * declared selection and a free-form answer cannot be combined.
 */
export function toggleOptionValue(
  selection: MultipleChoiceSelection,
  variant: MultipleChoiceVariant,
  value: string
): MultipleChoiceSelection {
  if (variant === 'singleSelect') {
    return { selectedValues: [value], isOtherSelected: false };
  }

  const isSelected = selection.selectedValues.includes(value);
  const selectedValues = isSelected
    ? selection.selectedValues.filter((v) => v !== value)
    : [...selection.selectedValues, value];
  return { selectedValues, isOtherSelected: false };
}

/**
 * Applies a click on the "Other" option. Selecting it clears every declared
 * option (see the exclusivity invariant on MultipleChoiceSelection).
 * Single-select keeps radio semantics — re-clicking "Other" leaves it
 * selected — while multi-select unchecks it.
 */
export function toggleOtherOption(
  selection: MultipleChoiceSelection,
  variant: MultipleChoiceVariant
): MultipleChoiceSelection {
  if (variant === 'multiSelect' && selection.isOtherSelected) {
    return EMPTY_MULTIPLE_CHOICE_SELECTION;
  }
  return { selectedValues: [], isOtherSelected: true };
}

/**
 * Whether the selection is submittable: at least one declared option, or
 * "Other" with a non-empty free-form answer.
 */
export function canSubmitMultipleChoiceSelection(
  selection: MultipleChoiceSelection,
  otherText: string
): boolean {
  if (selection.isOtherSelected) {
    return otherText.trim().length > 0;
  }
  return selection.selectedValues.length > 0;
}

/**
 * Builds the SUBMIT respond-mutation input. An "Other" answer sends only
 * `freeFormText`; a declared-option answer sends only `responseOptionValues`
 * — the backend rejects the two combined.
 */
export function buildMultipleChoiceSubmitInput(
  messageId: string,
  selection: MultipleChoiceSelection,
  otherText: string
): HouseholdRespondToChatMessageInput {
  if (selection.isOtherSelected) {
    return {
      messageID: messageId,
      responseAction: MessageResponseAction.Submit,
      freeFormText: otherText.trim(),
    };
  }
  return {
    messageID: messageId,
    responseAction: MessageResponseAction.Submit,
    responseOptionValues: selection.selectedValues,
  };
}
